import './RecipeGrid.css';
import React from 'react';
import { getRecipeImageUrl, formatMinutes } from '../../utils/recipeUtils';
import { useLanguage } from '../../i18n/LanguageContext';
import ShareButton from './ShareButton';

const RecipeGrid = ({
  recipes,
  isLoading,
  error,
  onRecipeSelect,
  onRecipeEdit,
  onRecipeDelete,
  currentUser = null,
  activeActionId = null,
  favoriteIds = null,
  onFavoriteToggle = null,
  emptyMessage = null,
  onShare = null,
}) => {
  const { lang, t } = useLanguage();
  const metaFallback = t.explorer.recipeMetaFallback;
  const favLabels = { add: t.favorites?.favoriteAdd ?? 'Save to favorites', remove: t.favorites?.favoriteRemove ?? 'Remove from favorites' };
  const nutritionTagLabels = t.nutrition?.tags ?? {};

  if (isLoading) {
    return (
      <section className="explorer-recipes" aria-label="Recipe listings">
        <div className="recipe-grid">
          <LoadingCard isHero />
          {[...Array(4)].map((_, index) => (
            <LoadingCard key={`loading-${index.toString()}`} />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="explorer-recipes" aria-live="assertive">
        <div className="recipe-empty-state">
          <p>Something went wrong while loading the archives.</p>
          <p className="hint">{error}</p>
        </div>
      </section>
    );
  }

  if (!recipes?.length) {
    return (
      <section className="explorer-recipes" aria-live="polite">
        <div className="recipe-empty-state">
          <p>{emptyMessage ?? 'No recipes found yet.'}</p>
          {!emptyMessage && <p className="hint">Be the first to contribute an artisanal masterpiece.</p>}
        </div>
      </section>
    );
  }

  const [heroRecipe, ...rest] = recipes;

  return (
    <section className="explorer-recipes" aria-label="Recipe listings">
      <div className="recipe-grid">
        <HeroRecipeCard
          recipe={heroRecipe}
          onSelect={onRecipeSelect}
          onEdit={onRecipeEdit}
          onDelete={onRecipeDelete}
          canManageRecipes={currentUser?.role === 'ADMIN' || currentUser?.id === heroRecipe?.recipeUserId}
          activeActionId={activeActionId}
          lang={lang}
          metaFallback={metaFallback}
          isFavorited={favoriteIds ? favoriteIds.has(heroRecipe._id || heroRecipe.id) : false}
          onFavoriteToggle={onFavoriteToggle}
          favLabels={favLabels}
          nutritionTagLabels={nutritionTagLabels}
          onShare={onShare}
        />
        {rest.map((recipe) => (
          <RecipeCard
            key={recipe._id || recipe.id}
            recipe={recipe}
            onSelect={onRecipeSelect}
            onEdit={onRecipeEdit}
            onDelete={onRecipeDelete}
            canManageRecipes={currentUser?.role === 'ADMIN' || currentUser?.id === recipe?.recipeUserId}
            activeActionId={activeActionId}
            lang={lang}
            metaFallback={metaFallback}
            isFavorited={favoriteIds ? favoriteIds.has(recipe._id || recipe.id) : false}
            onFavoriteToggle={onFavoriteToggle}
            favLabels={favLabels}
            nutritionTagLabels={nutritionTagLabels}
            onShare={onShare}
          />
        ))}
      </div>
    </section>
  );
};

const LoadingCard = ({ isHero = false }) => (
  <article className={`recipe-card${isHero ? ' hero-card' : ''}`} aria-hidden="true">
    <div className={`recipe-media${isHero ? ' hero-media' : ''} skeleton-block`} />
    <div className={`recipe-copy${isHero ? ' hero-copy' : ''}`}>
      <span className="recipe-meta skeleton-line" />
      <span className="skeleton-line" />
      <span className="skeleton-line short" />
    </div>
  </article>
);

const NUTRITION_TAG_STYLE = {
  gluten_free: 'tag--green',
  high_calorie: 'tag--red',
  high_protein: 'tag--blue',
  high_carbs: 'tag--amber',
  high_sugar: 'tag--red',
  dietary_fiber: 'tag--green',
  high_fat: 'tag--amber',
};

const renderNutritionTags = (recipe, labels) => {
  const tags = recipe?.nutrition?.nutritionTags;
  if (!tags?.length) return null;
  return (
    <div className="recipe-nutrition-tags" onClick={(e) => e.stopPropagation()} aria-label="Nutrition tags">
      {tags.map((tag) => (
        <span key={tag} className={`recipe-ntag ${NUTRITION_TAG_STYLE[tag] ?? 'tag--grey'}`}>
          {labels[tag] ?? tag}
        </span>
      ))}
    </div>
  );
};

const HeroRecipeCard = ({ recipe, onSelect, onEdit, onDelete, canManageRecipes, activeActionId, lang, metaFallback, isFavorited, onFavoriteToggle, favLabels, nutritionTagLabels, onShare }) => (
  <article
    className={`recipe-card hero-card${onSelect ? ' recipe-card-actionable' : ''}`}
    role={onSelect ? 'button' : undefined}
    tabIndex={onSelect ? 0 : undefined}
    onClick={onSelect ? () => onSelect(recipe) : undefined}
    onKeyDown={onSelect ? (event) => handleKeyActivate(event, () => onSelect(recipe)) : undefined}
  >
    <div className="recipe-media hero-media">
      <img src={getRecipeImageUrl(recipe)} alt={recipe.title} loading="lazy" />
      {onShare && <button className="recipe-share-overlay-btn" type="button" aria-label="Share recipe" onClick={e => { e.stopPropagation(); onShare(recipe); }}><span className="material-symbols-rounded" aria-hidden="true">share</span></button>}
      <div className="recipe-card-top-actions">
        {renderFavoriteButton({ recipe, isFavorited, onFavoriteToggle, favLabels })}
        {renderManagementActions({ recipe, onEdit, onDelete, canManageRecipes, activeActionId })}
      </div>
      {recipe.tags?.length ? <span className="recipe-badge">{recipe.tags[0]}</span> : null}
    </div>
    <div className="recipe-copy hero-copy">
      <span className="recipe-meta">{formatMeta(recipe, lang, metaFallback)}</span>
      <h2>{recipe.title}</h2>
      <p>{recipe.description}</p>
      {renderNutritionTags(recipe, nutritionTagLabels)}
    </div>
  </article>
);

const RecipeCard = ({ recipe, onSelect, onEdit, onDelete, canManageRecipes, activeActionId, lang, metaFallback, isFavorited, onFavoriteToggle, favLabels, nutritionTagLabels, onShare }) => (
  <article
    className={`recipe-card${onSelect ? ' recipe-card-actionable' : ''}`}
    role={onSelect ? 'button' : undefined}
    tabIndex={onSelect ? 0 : undefined}
    onClick={onSelect ? () => onSelect(recipe) : undefined}
    onKeyDown={onSelect ? (event) => handleKeyActivate(event, () => onSelect(recipe)) : undefined}
  >
    <div className="recipe-media">
      <img src={getRecipeImageUrl(recipe)} alt={recipe.title} loading="lazy" />
      {onShare && <button className="recipe-share-overlay-btn" type="button" aria-label="Share recipe" onClick={e => { e.stopPropagation(); onShare(recipe); }}><span className="material-symbols-rounded" aria-hidden="true">share</span></button>}
      <div className="recipe-card-top-actions">
        {renderFavoriteButton({ recipe, isFavorited, onFavoriteToggle, favLabels })}
        {renderManagementActions({ recipe, onEdit, onDelete, canManageRecipes, activeActionId })}
      </div>
    </div>
    <div className="recipe-copy">
      <span className="recipe-meta">{formatMeta(recipe, lang, metaFallback)}</span>
      <h3>{recipe.title}</h3>
      <p>{recipe.description}</p>
      {renderNutritionTags(recipe, nutritionTagLabels)}
    </div>
  </article>
);

const renderFavoriteButton = ({ recipe, isFavorited, onFavoriteToggle, favLabels }) => {
  if (!onFavoriteToggle) {
    return null;
  }

  return (
    <button
      type="button"
      className={`recipe-fav-button${isFavorited ? ' is-favorited' : ''}`}
      aria-label={isFavorited ? favLabels.remove : favLabels.add}
      onClick={(event) => {
        event.stopPropagation();
        onFavoriteToggle(recipe);
      }}
    >
      <span className="material-symbols-rounded" aria-hidden="true">
        {isFavorited ? 'favorite' : 'favorite_border'}
      </span>
    </button>
  );
};

const renderManagementActions = ({ recipe, onEdit, onDelete, canManageRecipes, activeActionId }) => {
  if (!canManageRecipes) {
    return null;
  }

  const recipeId = recipe?._id || recipe?.id;
  const isDeleting = activeActionId === recipeId;

  return (
    <>
      <button
        type="button"
        className="recipe-action-button"
        aria-label={`Edit recipe ${recipe.title}`}
        onClick={(event) => {
          event.stopPropagation();
          onEdit?.(recipe);
        }}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          edit
        </span>
      </button>
      <button
        type="button"
        className="recipe-action-button danger"
        aria-label={`Delete recipe ${recipe.title}`}
        onClick={(event) => {
          event.stopPropagation();
          onDelete?.(recipe);
        }}
        disabled={isDeleting}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          {isDeleting ? 'hourglass_top' : 'delete'}
        </span>
      </button>
    </>
  );
};

const handleKeyActivate = (event, callback) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

const formatMeta = (recipe, lang = 'en', fallback = 'Editorial Nourishment') => {
  const parts = [];
  if (recipe.tags?.length) {
    parts.push(recipe.tags[0]);
  }
  if (recipe.prepTime || recipe.cookTime) {
    const total = Number(recipe.prepTime || 0) + Number(recipe.cookTime || 0);
    if (total) {
      parts.push(formatMinutes(total, lang));
    }
  }
  if (recipe.recipeUserName) {
    parts.push(recipe.recipeUserName);
  }
  return parts.join(' • ') || fallback;
};

export default RecipeGrid;
